/**
 * PDF Storyboard Export
 *
 * Creates professional storyboard PDFs with images and script content.
 * Supports multiple layout options for different use cases.
 */

import { jsPDF } from 'jspdf';
import type { BeatNode, AspectRatio, TitleNode, CameraOverlay } from '@/types';
import { getImageUrl } from './browserAPI';
import { fitImageToAspectRatio, blobToDataURL } from './imageProcessing';
import { APP_NAME } from '@/constants/app';
import { compositeOverlaysOnImage, canvasToDataURL, compositeTitleOverlays, createWhiteCanvasWithTitles } from './overlayComposite';

// =============================================================================
// TYPES
// =============================================================================

export type PDFLayoutStyle =
  | 'page-per-beat' // One beat per page (large image, full script)
  | 'two-up' // Two beats per page (side by side)
  | 'four-up' // Four beats per page (2x2 grid)
  | 'six-up'; // Six beats per page (2x3 grid)

export interface PDFExportOptions {
  projectName: string;
  fps: number;
  aspectRatio: AspectRatio;
  nodes: BeatNode[];
  titleNodes?: TitleNode[];
  layout?: PDFLayoutStyle;
  includeTimecodes?: boolean;
  includeCoverPage?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const PAGE_WIDTH = 210; // A4 width in mm
const PAGE_HEIGHT = 297; // A4 height in mm
const MARGIN = 15; // Page margins in mm

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get timeline nodes (exclude shelved beats, sort by x-position)
 */
function getTimelineNodes(nodes: BeatNode[]): BeatNode[] {
  return nodes
    .filter((node) => !node.data.onShelf)
    .sort((a, b) => a.position.x - b.position.x);
}

/**
 * Convert seconds to timecode string (HH:MM:SS:FF)
 */
function secondsToTimecode(seconds: number, fps: number): string {
  const totalFrames = Math.floor(seconds * fps);
  const frames = totalFrames % fps;
  const totalSeconds = Math.floor(totalFrames / fps);
  const secs = totalSeconds % 60;
  const mins = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);

  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(frames).padStart(2, '0')}`;
}

/**
 * Load image from blob URL or path
 */
async function loadImage(imagePath: string | null): Promise<string | null> {
  if (!imagePath) return null;

  // Use getImageUrl which checks both imageStore and blobManager
  return getImageUrl(imagePath);
}

/**
 * Convert image to base64 for PDF embedding with aspect ratio and offset applied
 */
async function imageToBase64(
  imageUrl: string,
  aspectRatio: AspectRatio,
  offsetX: number = 0,
  offsetY: number = 0,
  scale: number = 1,
  overlays?: CameraOverlay[],
  titleNodes?: TitleNode[]
): Promise<string> {
  // Process image with aspect ratio and offset
  const processedBlob = await fitImageToAspectRatio(
    imageUrl,
    aspectRatio,
    1280, // Reduced for reasonable PDF file size
    offsetX,
    offsetY,
    scale
  );

  // Convert blob to data URL
  const baseImageDataUrl = await blobToDataURL(processedBlob);

  // If no overlays and no titles, return the base image
  const hasOverlays = overlays && overlays.length > 0;
  const hasTitles = titleNodes && titleNodes.length > 0;
  
  if (!hasOverlays && !hasTitles) {
    return baseImageDataUrl;
  }

  // Composite overlays and titles onto the image
  const width = 1280;
  const height = aspectRatio === '16:9' ? 720 : aspectRatio === '1:1' ? 1280 : Math.round(1280 * (16 / 9));

  try {
    let canvas = await compositeOverlaysOnImage(
      baseImageDataUrl,
      overlays || [],
      aspectRatio,
      width,
      height
    );
    
    // Composite title overlays if present
    if (hasTitles) {
      canvas = compositeTitleOverlays(canvas, titleNodes!);
    }
    
    return canvasToDataURL(canvas);
  } catch (error) {
    console.error('Failed to composite overlays, returning base image:', error);
    return baseImageDataUrl;
  }
}

/**
 * Wrap text to fit within a width
 */
function wrapText(
  doc: jsPDF,
  text: string,
  maxWidth: number,
  fontSize: number = 10
): string[] {
  doc.setFontSize(fontSize);
  const lines: string[] = [];
  const paragraphs = text.split('\n');

  for (const paragraph of paragraphs) {
    if (!paragraph.trim()) {
      lines.push('');
      continue;
    }

    const words = paragraph.split(' ');
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const width = doc.getTextWidth(testLine);

      if (width > maxWidth) {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          lines.push(word);
          currentLine = '';
        }
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }
  }

  return lines;
}

// =============================================================================
// COVER PAGE
// =============================================================================

function addCoverPage(doc: jsPDF, projectName: string): void {
  const centerX = PAGE_WIDTH / 2;
  const centerY = PAGE_HEIGHT / 2;

  // Title
  doc.setFontSize(32);
  doc.setFont('helvetica', 'bold');
  doc.text(projectName, centerX, centerY - 20, { align: 'center' });

  // Subtitle
  doc.setFontSize(16);
  doc.setFont('helvetica', 'normal');
  doc.text('Storyboard', centerX, centerY, { align: 'center' });

  // Date
  doc.setFontSize(12);
  doc.text(new Date().toLocaleDateString(), centerX, centerY + 20, {
    align: 'center',
  });

  // Footer
  doc.setFontSize(10);
  doc.text(
    `Generated with ${APP_NAME}`,
    centerX,
    PAGE_HEIGHT - MARGIN,
    { align: 'center' }
  );
}

// =============================================================================
// PAGE-PER-BEAT LAYOUT
// =============================================================================

async function exportPagePerBeat(
  doc: jsPDF,
  nodes: BeatNode[],
  options: PDFExportOptions
): Promise<void> {
  let currentTime = 0;
  const titleNodes = options.titleNodes || [];

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const { title, sceneHeading, script, imagePath, duration } = node.data;

    if (i > 0) doc.addPage();

    let y = MARGIN;

    // Beat number and title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`Beat ${i + 1}: ${title}`, MARGIN, y);
    y += 8;

    // Calculate beat time range
    const beatStartTime = currentTime;
    const beatEndTime = beatStartTime + duration;

    // Timecode (if enabled)
    if (options.includeTimecodes) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const tcIn = secondsToTimecode(beatStartTime, options.fps);
      currentTime += duration;
      const tcOut = secondsToTimecode(currentTime, options.fps);
      doc.text(`${tcIn} - ${tcOut} (${duration.toFixed(2)}s)`, MARGIN, y);
      y += 6;
    } else {
      currentTime += duration;
    }

    // Scene heading
    if (sceneHeading) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(sceneHeading.toUpperCase(), MARGIN, y);
      y += 7;
    }

    // Find titles that overlap with this beat's time range
    const overlappingTitles = titleNodes.filter((titleNode) => {
      const titleStart = titleNode.data.startTime;
      const titleEnd = titleStart + titleNode.data.duration;
      // Check if title overlaps with beat time range
      return titleStart < beatEndTime && titleEnd > beatStartTime;
    });

    // Image (if available)
    if (imagePath) {
      const imageUrl = await loadImage(imagePath);
      if (imageUrl) {
        try {
          const offsetX = node.data.imageOffsetX || 0;
          const offsetY = node.data.imageOffsetY || 0;
          const scale = node.data.imageScale || 1;
          const overlays = node.data.overlays;
          const base64 = await imageToBase64(imageUrl, options.aspectRatio, offsetX, offsetY, scale, overlays, overlappingTitles);

          // Calculate image dimensions based on project aspect ratio
          // For portrait (9:16), constrain by height to prevent overflow
          // For landscape (16:9), constrain by width
          let imgWidth: number;
          let imgHeight: number;
          const aspectRatioValue = options.aspectRatio === '16:9' ? 16 / 9 : 9 / 16;

          if (options.aspectRatio === '9:16') {
            // Portrait: constrain by height based on available space
            // Reserve 60mm for script content minimum
            const availableHeight = PAGE_HEIGHT - y - MARGIN - 60;
            imgHeight = Math.min(120, availableHeight);
            imgWidth = imgHeight * aspectRatioValue;
          } else {
            // Landscape: constrain by width
            imgWidth = PAGE_WIDTH - MARGIN * 2;
            imgHeight = imgWidth / aspectRatioValue;
          }

          doc.addImage(base64, 'JPEG', MARGIN, y, imgWidth, imgHeight);
          y += imgHeight + 20; // Add spacing after image (20mm = ~3/4 inch)
        } catch (error) {
          console.error('Failed to add image:', error);
          // Draw placeholder with correct aspect ratio
          let imgWidth: number;
          let imgHeight: number;
          const aspectRatioValue = options.aspectRatio === '16:9' ? 16 / 9 : 9 / 16;

          if (options.aspectRatio === '9:16') {
            // Portrait: constrain by height based on available space
            // Reserve 60mm for script content minimum
            const availableHeight = PAGE_HEIGHT - y - MARGIN - 60;
            imgHeight = Math.min(120, availableHeight);
            imgWidth = imgHeight * aspectRatioValue;
          } else {
            // Landscape: constrain by width
            imgWidth = PAGE_WIDTH - MARGIN * 2;
            imgHeight = imgWidth / aspectRatioValue;
          }

          doc.setDrawColor(200);
          doc.rect(MARGIN, y, imgWidth, imgHeight);
          y += imgHeight + 20; // Add spacing after placeholder (20mm = ~3/4 inch)
        }
      }
    } else if (overlappingTitles.length > 0) {
      // No image but has titles - create white background with titles
      try {
        const canvas = createWhiteCanvasWithTitles(overlappingTitles, options.aspectRatio);
        const base64 = canvasToDataURL(canvas);
        
        // Calculate image dimensions based on project aspect ratio
        let imgWidth: number;
        let imgHeight: number;
        const aspectRatioValue = options.aspectRatio === '16:9' ? 16 / 9 : 9 / 16;

        if (options.aspectRatio === '9:16') {
          const availableHeight = PAGE_HEIGHT - y - MARGIN - 60;
          imgHeight = Math.min(120, availableHeight);
          imgWidth = imgHeight * aspectRatioValue;
        } else {
          imgWidth = PAGE_WIDTH - MARGIN * 2;
          imgHeight = imgWidth / aspectRatioValue;
        }

        doc.addImage(base64, 'JPEG', MARGIN, y, imgWidth, imgHeight);
        y += imgHeight + 20;
      } catch (error) {
        console.error('Failed to create title canvas:', error);
      }
    }

    // Script content
    if (script) {
      // Add extra spacing before script if there was an image
      if (imagePath) {
        y += 5; // Additional gap between image and script text
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const maxWidth = PAGE_WIDTH - MARGIN * 2;
      const lines = wrapText(doc, script, maxWidth, 10);

      for (const line of lines) {
        if (y > PAGE_HEIGHT - MARGIN - 5) {
          doc.addPage();
          y = MARGIN;
        }
        doc.text(line, MARGIN, y);
        y += 5;
      }
    }

    // Page number
    doc.setFontSize(8);
    doc.text(
      `Page ${doc.getCurrentPageInfo().pageNumber}`,
      PAGE_WIDTH / 2,
      PAGE_HEIGHT - MARGIN / 2,
      { align: 'center' }
    );
  }
}

// =============================================================================
// GRID LAYOUTS (2-up, 4-up, 6-up)
// =============================================================================

async function exportGridLayout(
  doc: jsPDF,
  nodes: BeatNode[],
  options: PDFExportOptions,
  cols: number,
  rows: number
): Promise<void> {
  const beatsPerPage = cols * rows;
  const cellWidth = (PAGE_WIDTH - MARGIN * 2) / cols;
  const cellHeight = (PAGE_HEIGHT - MARGIN * 2 - 15) / rows; // Reserve space for header/footer

  let currentTime = 0;
  const titleNodes = options.titleNodes || [];

  for (let pageStart = 0; pageStart < nodes.length; pageStart += beatsPerPage) {
    if (pageStart > 0) doc.addPage();

    // Page header
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(options.projectName, MARGIN, MARGIN - 5);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Page ${doc.getCurrentPageInfo().pageNumber}`,
      PAGE_WIDTH - MARGIN,
      MARGIN - 5,
      { align: 'right' }
    );

    // Draw beats in grid
    for (let i = 0; i < beatsPerPage; i++) {
      const nodeIndex = pageStart + i;
      if (nodeIndex >= nodes.length) break;

      const node = nodes[nodeIndex];
      const { title, script, imagePath, duration } = node.data;

      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = MARGIN + col * cellWidth;
      const y = MARGIN + row * cellHeight;

      // Cell border
      doc.setDrawColor(200);
      doc.rect(x, y, cellWidth, cellHeight);

      // Beat number and title
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      const beatText = `${nodeIndex + 1}. ${title}`;
      const titleLines = wrapText(doc, beatText, cellWidth - 4, 9);
      let textY = y + 4;
      for (let j = 0; j < Math.min(titleLines.length, 2); j++) {
        doc.text(titleLines[j], x + 2, textY);
        textY += 4;
      }

      // Calculate beat time range
      const beatStartTime = currentTime;
      const beatEndTime = beatStartTime + duration;

      // Timecode (if enabled)
      if (options.includeTimecodes) {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        const tcIn = secondsToTimecode(currentTime, options.fps);
        doc.text(tcIn, x + 2, textY);
        textY += 3;
      }
      currentTime += duration;

      // Find titles that overlap with this beat's time range
      const overlappingTitles = titleNodes.filter((titleNode) => {
        const titleStart = titleNode.data.startTime;
        const titleEnd = titleStart + titleNode.data.duration;
        // Check if title overlaps with beat time range
        return titleStart < beatEndTime && titleEnd > beatStartTime;
      });

      // Image - calculate dimensions based on project aspect ratio
      // For portrait (9:16), constrain by height to fit in cell
      // For landscape (16:9), constrain by width
      let imageWidth: number;
      let imageHeight: number;
      const aspectRatioValue = options.aspectRatio === '16:9' ? 16 / 9 : 9 / 16;

      if (options.aspectRatio === '9:16') {
        // Portrait: constrain by height (50% of cell height)
        imageHeight = cellHeight * 0.5;
        imageWidth = imageHeight * aspectRatioValue;
      } else {
        // Landscape: constrain by width
        imageWidth = cellWidth - 4;
        imageHeight = imageWidth / aspectRatioValue;
      }

      if (imagePath) {
        const imageUrl = await loadImage(imagePath);
        if (imageUrl) {
          try {
            const offsetX = node.data.imageOffsetX || 0;
            const offsetY = node.data.imageOffsetY || 0;
            const scale = node.data.imageScale || 1;
            const overlays = node.data.overlays;
            const base64 = await imageToBase64(imageUrl, options.aspectRatio, offsetX, offsetY, scale, overlays, overlappingTitles);
            doc.addImage(
              base64,
              'JPEG',
              x + 2,
              textY,
              imageWidth,
              imageHeight
            );
          } catch (error) {
            console.error('Failed to add image:', error);
          }
        }
      } else if (overlappingTitles.length > 0) {
        // No image but has titles - create white background with titles
        try {
          const canvas = createWhiteCanvasWithTitles(overlappingTitles, options.aspectRatio);
          const base64 = canvasToDataURL(canvas);
          doc.addImage(
            base64,
            'JPEG',
            x + 2,
            textY,
            imageWidth,
            imageHeight
          );
        } catch (error) {
          console.error('Failed to create title canvas:', error);
        }
      }
      textY += imageHeight + 2;

      // Script preview (limited lines)
      if (script) {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        const scriptLines = wrapText(doc, script, cellWidth - 4, 7);
        const maxLines = Math.floor((y + cellHeight - textY - 2) / 3);
        for (let j = 0; j < Math.min(scriptLines.length, maxLines); j++) {
          if (textY > y + cellHeight - 3) break;
          doc.text(scriptLines[j], x + 2, textY);
          textY += 3;
        }
      }
    }
  }
}

// =============================================================================
// MAIN EXPORT FUNCTION
// =============================================================================

export async function exportToPDF(options: PDFExportOptions): Promise<Blob> {
  const {
    projectName,
    nodes,
    layout = 'page-per-beat',
    includeCoverPage = true,
  } = options;

  const timelineNodes = getTimelineNodes(nodes);

  if (timelineNodes.length === 0) {
    throw new Error('No beats in timeline to export');
  }

  // Create PDF document (A4, portrait)
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  // Add cover page
  if (includeCoverPage) {
    addCoverPage(doc, projectName);
    doc.addPage();
  }

  // Add content based on layout
  switch (layout) {
    case 'page-per-beat':
      await exportPagePerBeat(doc, timelineNodes, options);
      break;
    case 'two-up':
      await exportGridLayout(doc, timelineNodes, options, 2, 1);
      break;
    case 'four-up':
      await exportGridLayout(doc, timelineNodes, options, 2, 2);
      break;
    case 'six-up':
      await exportGridLayout(doc, timelineNodes, options, 2, 3);
      break;
  }

  // Return PDF as blob
  return doc.output('blob');
}

// =============================================================================
// SCRIPT-ONLY PDF EXPORT (Screenplay Format)
// =============================================================================

export interface ScriptPDFOptions {
  projectName: string;
  nodes: BeatNode[];
  author?: string;
  description?: string;
}

// Screenplay formatting constants (in mm, based on US Letter but adapted for A4)
// Standard screenplay margins: 1.5" left, 1" right, 1" top/bottom
const SCRIPT_MARGIN_LEFT = 38; // ~1.5 inches
const SCRIPT_MARGIN_RIGHT = 25; // ~1 inch
const SCRIPT_MARGIN_TOP = 25; // ~1 inch
const SCRIPT_MARGIN_BOTTOM = 25; // ~1 inch
const SCRIPT_LINE_HEIGHT = 4.2; // Courier 12pt line height (~1/6 inch)

// Dialogue formatting (narrower margins for dialogue blocks)
const DIALOGUE_MARGIN_LEFT = 63; // ~2.5 inches from left
const DIALOGUE_MARGIN_RIGHT = 50; // ~2 inches from right
const CHARACTER_MARGIN_LEFT = 95; // ~3.7 inches from left (centered on page)

/**
 * Add screenplay-style title page
 */
function addScreenplayTitlePage(
  doc: jsPDF,
  projectName: string,
  author?: string,
  description?: string
): void {
  const centerX = PAGE_WIDTH / 2;
  let yPos = PAGE_HEIGHT / 3;

  // Title at ~1/3 down the page
  doc.setFontSize(12);
  doc.setFont('courier', 'bold');
  doc.text(projectName.toUpperCase(), centerX, yPos, { align: 'center' });
  yPos += 15;

  // "Written by" line and author (only if author is provided)
  if (author && author.trim()) {
    doc.setFont('courier', 'normal');
    doc.text('Written by', centerX, yPos, { align: 'center' });
    yPos += 10;
    doc.text(author.trim(), centerX, yPos, { align: 'center' });
    yPos += 15;
  }

  // Description (max 5 lines, truncated with "...")
  if (description && description.trim()) {
    doc.setFont('courier', 'normal');
    const descLines = wrapScreenplayText(doc, description.trim(), 100);
    const maxDescLines = 5;
    const displayLines = descLines.slice(0, maxDescLines);
    if (descLines.length > maxDescLines) {
      displayLines[maxDescLines - 1] = displayLines[maxDescLines - 1].slice(0, -3) + '...';
    }
    displayLines.forEach((line) => {
      doc.text(line, centerX, yPos, { align: 'center' });
      yPos += 5;
    });
  }

  // Footer with generation info (bottom right, industry standard)
  doc.setFontSize(10);
  doc.text(
    new Date().toLocaleDateString(),
    PAGE_WIDTH - SCRIPT_MARGIN_RIGHT,
    PAGE_HEIGHT - SCRIPT_MARGIN_BOTTOM,
    { align: 'right' }
  );
}

/**
 * Wrap text for screenplay format with specified width
 */
function wrapScreenplayText(
  doc: jsPDF,
  text: string,
  maxWidth: number
): string[] {
  doc.setFontSize(12);
  doc.setFont('courier', 'normal');
  const lines: string[] = [];
  const paragraphs = text.split('\n');

  for (const paragraph of paragraphs) {
    if (!paragraph.trim()) {
      lines.push('');
      continue;
    }

    const words = paragraph.split(' ');
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const width = doc.getTextWidth(testLine);

      if (width > maxWidth) {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          lines.push(word);
          currentLine = '';
        }
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }
  }

  return lines;
}

/**
 * Parse script text to identify character names, dialogue, and action
 * Simple heuristic: lines in ALL CAPS followed by dialogue are character cues
 */
function parseScriptElements(script: string): Array<{
  type: 'action' | 'character' | 'dialogue' | 'parenthetical';
  text: string;
}> {
  const elements: Array<{
    type: 'action' | 'character' | 'dialogue' | 'parenthetical';
    text: string;
  }> = [];

  const lines = script.split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (!line) {
      i++;
      continue;
    }

    // Check if this is a character cue (ALL CAPS, followed by dialogue or parenthetical)
    // Character names are typically short (under 40 chars) and all caps
    const isAllCaps = line === line.toUpperCase() && /^[A-Z][A-Z\s\.\-\']+$/.test(line);
    const isShortEnough = line.length < 40;
    const nextLine = lines[i + 1]?.trim() || '';
    const hasFollowingContent = nextLine.length > 0;

    if (isAllCaps && isShortEnough && hasFollowingContent) {
      // This looks like a character cue
      elements.push({ type: 'character', text: line });
      i++;

      // Check for parenthetical (starts with '(' )
      while (i < lines.length) {
        const dialogueLine = lines[i].trim();
        if (!dialogueLine) {
          i++;
          break;
        }

        if (dialogueLine.startsWith('(') && dialogueLine.endsWith(')')) {
          elements.push({ type: 'parenthetical', text: dialogueLine });
        } else if (dialogueLine === dialogueLine.toUpperCase() && dialogueLine.length < 40) {
          // New character cue, don't consume
          break;
        } else {
          elements.push({ type: 'dialogue', text: dialogueLine });
        }
        i++;
      }
    } else {
      // Action/description line
      elements.push({ type: 'action', text: line });
      i++;
    }
  }

  return elements;
}

/**
 * Export script content as a properly formatted screenplay PDF
 * Uses Courier 12pt font and standard screenplay margins
 */
export async function exportScriptToPDF(options: ScriptPDFOptions): Promise<Blob> {
  const { projectName, nodes, author, description } = options;

  const timelineNodes = getTimelineNodes(nodes);

  if (timelineNodes.length === 0) {
    throw new Error('No beats in timeline to export');
  }

  // Create PDF document (A4, portrait)
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  // Title page (screenplay format)
  addScreenplayTitlePage(doc, projectName, author, description);
  doc.addPage();

  let y = SCRIPT_MARGIN_TOP;
  const actionWidth = PAGE_WIDTH - SCRIPT_MARGIN_LEFT - SCRIPT_MARGIN_RIGHT;
  const dialogueWidth = PAGE_WIDTH - DIALOGUE_MARGIN_LEFT - DIALOGUE_MARGIN_RIGHT;

  // Process each beat
  for (let i = 0; i < timelineNodes.length; i++) {
    const node = timelineNodes[i];
    const { sceneHeading, script } = node.data;

    // Scene heading (slugline) - UPPERCASE, left margin
    if (sceneHeading) {
      // Check if we need a new page (scene headings shouldn't be orphaned)
      if (y > PAGE_HEIGHT - SCRIPT_MARGIN_BOTTOM - 30) {
        doc.addPage();
        y = SCRIPT_MARGIN_TOP;
      }

      // Add blank line before new scene (except first)
      if (i > 0) {
        y += SCRIPT_LINE_HEIGHT * 2;
      }

      doc.setFontSize(12);
      doc.setFont('courier', 'bold');
      doc.text(sceneHeading.toUpperCase(), SCRIPT_MARGIN_LEFT, y);
      y += SCRIPT_LINE_HEIGHT * 2; // Double space after scene heading
    }

    // Script content - parse and format
    if (script) {
      const elements = parseScriptElements(script);

      for (const element of elements) {
        doc.setFontSize(12);

        switch (element.type) {
          case 'action':
            // Action is at left margin, normal font
            doc.setFont('courier', 'normal');
            const actionLines = wrapScreenplayText(doc, element.text, actionWidth);

            for (const line of actionLines) {
              if (y > PAGE_HEIGHT - SCRIPT_MARGIN_BOTTOM) {
                doc.addPage();
                y = SCRIPT_MARGIN_TOP;
              }
              doc.text(line, SCRIPT_MARGIN_LEFT, y);
              y += SCRIPT_LINE_HEIGHT;
            }
            y += SCRIPT_LINE_HEIGHT; // Extra line after action block
            break;

          case 'character':
            // Character names are centered (roughly)
            if (y > PAGE_HEIGHT - SCRIPT_MARGIN_BOTTOM - 20) {
              doc.addPage();
              y = SCRIPT_MARGIN_TOP;
            }
            y += SCRIPT_LINE_HEIGHT; // Blank line before character
            doc.setFont('courier', 'bold');
            doc.text(element.text, CHARACTER_MARGIN_LEFT, y);
            y += SCRIPT_LINE_HEIGHT;
            break;

          case 'parenthetical':
            // Parentheticals are indented, in parentheses
            doc.setFont('courier', 'normal');
            if (y > PAGE_HEIGHT - SCRIPT_MARGIN_BOTTOM) {
              doc.addPage();
              y = SCRIPT_MARGIN_TOP;
            }
            doc.text(element.text, DIALOGUE_MARGIN_LEFT + 10, y);
            y += SCRIPT_LINE_HEIGHT;
            break;

          case 'dialogue':
            // Dialogue has narrower margins
            doc.setFont('courier', 'normal');
            const dialogueLines = wrapScreenplayText(doc, element.text, dialogueWidth);

            for (const line of dialogueLines) {
              if (y > PAGE_HEIGHT - SCRIPT_MARGIN_BOTTOM) {
                doc.addPage();
                y = SCRIPT_MARGIN_TOP;
              }
              doc.text(line, DIALOGUE_MARGIN_LEFT, y);
              y += SCRIPT_LINE_HEIGHT;
            }
            break;
        }
      }
    }
  }

  // Add page numbers (top right), starting from page 2 of content
  const pageCount = doc.getNumberOfPages();
  for (let i = 2; i <= pageCount; i++) { // Skip title page
    doc.setPage(i);
    doc.setFontSize(12);
    doc.setFont('courier', 'normal');
    doc.text(
      `${i - 1}.`,
      PAGE_WIDTH - SCRIPT_MARGIN_RIGHT,
      SCRIPT_MARGIN_TOP - 10,
      { align: 'right' }
    );
  }

  // Add footer to title page (page 1)
  doc.setPage(1);
  doc.setFontSize(10);
  doc.setFont('courier', 'normal');
  doc.text(
    'Made with Emberboard',
    SCRIPT_MARGIN_LEFT,
    PAGE_HEIGHT - SCRIPT_MARGIN_BOTTOM,
    { align: 'left' }
  );

  return doc.output('blob');
}
